/**
 * String diff utils lifted from Editor-SDK client-textutils:
 * https://office.visualstudio.com/ISS/_git/Editor-SDK?path=/packages/client-textutils/src/StringUtils.ts
 */
export function getMinimalDifferenceBounds(oldText, newText, excludeNewlineFromWhitespace) {
    if (excludeNewlineFromWhitespace === void 0) { excludeNewlineFromWhitespace = false; }
    // initialize the result as if no changes have happened
    var result = {
        firstDiffLeft: 0,
        firstDiffRight: newText.length,
    };
    // Start by trying to see if we just typed at the beginning or end.
    // This should (hopefully) be blazingly fast because it gets evaluated natively, and typing
    // at the end of an old tracked change is likely a common case.
    var shortString;
    var longString;
    if (oldText.length < newText.length) {
        shortString = oldText;
        longString = newText;
    }
    else {
        shortString = newText;
        longString = oldText;
    }
    if (longString.indexOf(shortString) === 0) {
        result.firstDiffLeft = shortString.length;
        result.firstDiffRight = 0;
    }
    else if (longString.endsWith(shortString)) {
        result.firstDiffLeft = 0;
        result.firstDiffRight = shortString.length;
    }
    else {
        result = getDifferenceBounds(oldText, newText, excludeNewlineFromWhitespace);
        // Sometimes two strings overlap, so the aggregate difference cannot be longer than the actual string.
        // Consider:
        //		old:  "book"	|	firstDiffLeft: 2
        //		new:  "bok"	  |	firstDiffRight: 2
        // In this case we need to change firstDiffRight to be at index 1 so that the "o" isn't shared.
        if (result.firstDiffLeft + result.firstDiffRight > shortString.length) {
            result.firstDiffRight = shortString.length - result.firstDiffLeft;
        }
    }
    trimDifferenceBounds(result, oldText, newText);
    return result;
}
function trimDifferenceBounds(diffRange, oldText, newText) {
    // Make sure we don't end in the middle of a surrogate pair
    if (diffRange.firstDiffLeft > 0) {
        var isAtOldSurrogatePair = diffRange.firstDiffLeft < oldText.length && isLowSurrogateCharAt(oldText, diffRange.firstDiffLeft);
        var isAtNewSurrogatePair = diffRange.firstDiffLeft < newText.length && isLowSurrogateCharAt(newText, diffRange.firstDiffLeft);
        if (isAtOldSurrogatePair || isAtNewSurrogatePair) {
            diffRange.firstDiffLeft -= 1;
        }
    }
    // Make sure we don't end in the middle of a surrogate pair
    if (diffRange.firstDiffRight > 1) {
        var isAtOldSurrogatePair = diffRange.firstDiffRight < oldText.length && isLowSurrogateCharAt(oldText, oldText.length - diffRange.firstDiffRight);
        var isAtNewSurrogatePair = diffRange.firstDiffRight < newText.length && isLowSurrogateCharAt(newText, newText.length - diffRange.firstDiffRight);
        if (isAtOldSurrogatePair || isAtNewSurrogatePair) {
            diffRange.firstDiffRight -= 1;
        }
    }
}
function getDifferenceBounds(oldText, newText, excludeNewlineFromWhitespace) {
    if (excludeNewlineFromWhitespace === void 0) { excludeNewlineFromWhitespace = false; }
    // initialize the result as if no changes have happened
    var result = {
        firstDiffLeft: 0,
        firstDiffRight: newText.length,
    };
    // Get the index of the first character that is different between the new and old strings.
    // Starting from the "left"
    var charIndex;
    for (charIndex = 0; charIndex < oldText.length && charIndex < newText.length; charIndex += 1) {
        var oldCharCode = oldText.charCodeAt(charIndex);
        var newCharCode = newText.charCodeAt(charIndex);
        // Are the characters different
        // In the meantime, make sure they are not convertiable whitespaces.
        // The main reason of it is that same whitespaces are sometimes changed in their encodings
        // by the websites while typing, like GMail.
        if (oldCharCode !== newCharCode && !(isWhitespace(oldCharCode, excludeNewlineFromWhitespace) && isWhitespace(newCharCode, excludeNewlineFromWhitespace))) {
            break;
        }
    }
    result.firstDiffLeft = charIndex;
    // Get the index of the first character that is different between the new and old strings
    // Starting from the "right"
    for (charIndex = 0; charIndex < oldText.length && charIndex < newText.length; charIndex += 1) {
        var oldCharCode = oldText.charCodeAt(oldText.length - charIndex - 1);
        var newCharCode = newText.charCodeAt(newText.length - charIndex - 1);
        // Are the characters different
        // In the meantime, make sure they are not convertiable whitespaces.
        // The main reason of it is that same whitespaces are sometimes changed in their encodings
        // by the websites while typing, like GMail.
        if (oldCharCode !== newCharCode && !(isWhitespace(oldCharCode, excludeNewlineFromWhitespace) && isWhitespace(newCharCode, excludeNewlineFromWhitespace))) {
            break;
        }
    }
    result.firstDiffRight = charIndex;
    return result;
}
/**
 * Returns true if the passed character is a whitespace character
 * Originally it was taken from Word code. Now it is modified to have New Lines chars removed
 * @param c Charachter to check.
 */
function isWhitespace(c, excludeNewlineFromWhitespace) {
    if (excludeNewlineFromWhitespace === void 0) { excludeNewlineFromWhitespace = false; }
    switch (c) {
        case 0x3000: // SpaceFE
        case 0x2005: // NarrowSpace
        case 0x0020: // Space
        case 0x000b: // CRJ:
        case 0x0009: // xchTab:
        case 0x00a0: // NonBreakSpace:
            return true;
        case 0x000d: // End of Paragraph
        case 0x000a: // End of Line
        case 0xfffc: // Object Replacement Space - Soft Line Breaks
            return !excludeNewlineFromWhitespace;
    }
    return false;
}
/**
 * Returns true if the character is the lower portion of a surrogate character pair.
 * @param s String to check.
 * @param i Index to check.
 */
function isLowSurrogateCharAt(s, i) {
    return s.charCodeAt(i) >= 0xdc00 && s.charCodeAt(i) <= 0xdfff;
}
//# sourceMappingURL=utils.js.map